import { Icons } from '@cockpitai/components/atoms'
import { Typography } from '@cockpitai/components/atoms/Typography'
import { routesPath } from '@cockpitai/constants/routesPath'
import { useChatStore } from '@cockpitai/store/chat'
import { useLevelNavigation } from '@cockpitai/store/navigation/useLevelNavigation'
import { useSideBarStore } from '@cockpitai/store/navigation/useSideBarStore'
import { goToScrollTop } from '@cockpitai/utils'
import { handleZendeskButtonClick } from '@cockpitai/utils/ZendeskHandler'
import { sendAnalyticsEvent } from '@cockpitai/utils/analytics'
import { isMobile } from '@cockpitai/utils/device'
import { urlDecipher } from '@cockpitai/utils/urlCipher'
import { memo, useCallback, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { BUSINESS_CODE } from '../Authorize'
import { useAuthorize } from '../Authorize/useAuthorize'
import { useSideBar } from './SideBarContext'
import { HomeButton } from './components/HomeButton'
import { Logo } from './components/Logo'
import { ToggleableMenu } from './components/ToggleableMenu'

type SidebarProps = {
  openSidebar: () => void
  closeSidebar: () => void
  sidebarIsOpen: boolean
  toggleSideBarLocked: () => void
  isLockedSideBar: boolean
  width: number | string
}

const SidebarButtons = memo(function SidebarButtons({
  newChat,
  sidebarIsOpen,
}: {
  resetChat: () => void
  newChat: () => void
  sidebarIsOpen: boolean
}) {
  return (
    <>
      <li className="help-button pl-[2px] text-sm">
        <button onClick={handleZendeskButtonClick}>
          <Icons.Question width={20} height={20} className="text-white" />
          <Typography className="text-white" weight="light">
            Suporte
          </Typography>
        </button>
      </li>
      <li>
        <NavLink
          to={routesPath.dashboard.chat}
          className="ia-link"
          onClick={newChat}
          style={{
            width: sidebarIsOpen || isMobile() ? '100%' : '35px',
          }}>
          <div className="margin-0 flex items-center gap-3">
            <Icons.Stars gradient={sidebarIsOpen || undefined} />
            <p>Fale com a AI</p>
          </div>
        </NavLink>
      </li>
    </>
  )
})

export function Sidebar({
  openSidebar,
  closeSidebar,
  sidebarIsOpen,
  toggleSideBarLocked,
  isLockedSideBar,
  width,
}: SidebarProps) {
  const { setIsStartedChat, clearMessages } = useChatStore()
  // const { user } = useSession()

  const { resetNavigation } = useLevelNavigation()

  const favorites = useSideBarStore(useCallback((state) => state.favorites, []))

  const handleMenuClick = (menuTitle: string, itemName: string) => {
    sendAnalyticsEvent('sidebar_navigation', {
      menu_section: menuTitle,
      item_name: itemName
    });
  };

  const favMenuIsOpen = useSideBarStore(
    useCallback((state) => state.favMenuIsOpen, []),
  )

  const recentChatMenuIsOpen = useSideBarStore(
    useCallback((state) => state.recentChatMenuIsOpen, []),
  )

  const visoesMenuIsOpen = useSideBarStore(
    useCallback((state) => state.visoesMenuIsOpen, []),
  )

  const { toggleRecentChatIsOpen, toggleVisoesIsOpen, toggleFavIsOpen } =
    useSideBarStore()

  const favPaths = useMemo(
    () =>
      favorites.map((fav) => ({
        name: fav.name,
        to: fav?.data
          ? urlDecipher(fav.data)
          : `?affiliateId=${fav.affiliateId}&occupationType=${fav.occupationType}` +
          (fav.withoutWallet ? '&withoutWallet=true' : ''),
        id: fav.id,
      })),
    [favorites],
  )

  const { setSidebarIsOpen } = useSideBar()

  const isAuthorized = useAuthorize({
    businessCode: [BUSINESS_CODE.ADMIN, BUSINESS_CODE.VEICULOS],
  })

  const resetChat = useCallback(() => {
    clearMessages()

    resetNavigation()

    setIsStartedChat(false)

    setSidebarIsOpen(false)

    goToScrollTop()
  }, [clearMessages, resetNavigation, setIsStartedChat, setSidebarIsOpen])

  const newChat = useCallback(() => {
    clearMessages()

    setIsStartedChat(false)

    goToScrollTop()

    setSidebarIsOpen(false)
  }, [clearMessages, setIsStartedChat, setSidebarIsOpen])

  const hideMktShare = true

  // const isNotSpecialistLevel = user?.occupationType && user?.occupationType <= EOccupationType.LEADER

  const visoesMenuItems = [
    { name: 'Números', to: routesPath.dashboard.bigNumbers },
    { name: 'Visões', to: routesPath.dashboard.bigViews },
    { name: 'Dispersão', to: routesPath.dashboard.bigQuestions },
    ...(import.meta.env.VITE_CONSULTANT !== 'true' ?
      [{ name: 'Fidelidade', to: routesPath.dashboard.fidelity }]
      : []),
    // ...(isNotSpecialistLevel ?
    //   [{ name: 'Hora por Hora', to: routesPath.dashboard.hourByHour }]
    //   : []),
    ...(isAuthorized && !hideMktShare
      ? [
        { name: 'Market Share', to: routesPath.dashboard.marketShare },
        { name: 'Conversão', to: routesPath.dashboard.conversions },
      ]
      : []),
    { name: 'Propostas D0', to: routesPath.dashboard.proposals },
    { name: 'Meus Painéis', to: routesPath.dashboard.myPanels },
  ]

  const openedState = () => {
    const allMenus = visoesMenuIsOpen && favMenuIsOpen && recentChatMenuIsOpen

    const noMenus = !visoesMenuIsOpen && !favMenuIsOpen && !recentChatMenuIsOpen

    const firstOnly =
      visoesMenuIsOpen && !favMenuIsOpen && !recentChatMenuIsOpen

    const secondOnly =
      !visoesMenuIsOpen && favMenuIsOpen && !recentChatMenuIsOpen

    const thirdOnly =
      !visoesMenuIsOpen && !favMenuIsOpen && recentChatMenuIsOpen

    const firstSecond =
      visoesMenuIsOpen && favMenuIsOpen && !recentChatMenuIsOpen

    const secondThird =
      !visoesMenuIsOpen && favMenuIsOpen && recentChatMenuIsOpen

    const firstThird =
      visoesMenuIsOpen && !favMenuIsOpen && recentChatMenuIsOpen

    if (favorites.length > 0) {
      if (allMenus) return 'all-opened'

      if (noMenus) return 'all-closed'

      if (firstOnly) return 'only-first-opened'

      if (secondOnly) return 'only-second-opened'

      if (thirdOnly) return 'only-third-opened'

      if (firstSecond) return 'first-second-opened'

      if (secondThird) return 'third-second-opened'

      if (firstThird) return 'first-third-opened'
    } else {
      if (visoesMenuIsOpen && recentChatMenuIsOpen) return 'all-opened'

      if (!visoesMenuIsOpen && !recentChatMenuIsOpen) return 'all-closed'

      return visoesMenuIsOpen ? 'only-first-opened' : 'only-second-opened'
    }
  }

  const handleMouseEnter = useCallback(() => {
    !isMobile() && openSidebar()
  }, [openSidebar])

  const handleMouseLeave = useCallback(() => {
    !isMobile() && closeSidebar()
  }, [closeSidebar])

  return (
    <aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ width }}>
      <Logo
        isLockedSideBar={isLockedSideBar}
        toggleSideBarLocked={toggleSideBarLocked}
        resetChat={resetChat}
      />
      <ul
        className={twMerge(
          'scroller-container mb-0 mt-10 grid space-y-2 overflow-hidden',
          favorites.length === 0 && 'with-two-items',
          openedState(),
        )}>
        <HomeButton resetChat={resetChat} sidebarIsOpen={sidebarIsOpen} />
        <ToggleableMenu
          title="Visões"
          icon={<Icons.LineChart width={20} height={20} />}
          menuItems={visoesMenuItems}
          isOpen={visoesMenuIsOpen}
          toggleIsOpen={() => {
            toggleVisoesIsOpen();
            handleMenuClick('Visões', 'menu_toggle');
          }}
          onItemClick={(item) => handleMenuClick('Visões', item.name)}
          resetChat={resetChat}
          notStartBreadcrumb
        />
        {favorites.length > 0 && (
          <ToggleableMenu
            title="Favoritos"
            icon={<Icons.Star width={20} height={20} />}
            isFavList
            menuItems={favPaths}
            isOpen={favMenuIsOpen}
            toggleIsOpen={() => {
              toggleFavIsOpen();
              handleMenuClick('Favoritos', 'menu_toggle');
            }}
            onItemClick={(item) => handleMenuClick('Favoritos', item.name)}
            resetChat={resetChat}
          />
        )}
        <ToggleableMenu
          title="Recentes"
          icon={<Icons.Clock width={22} height={22} className="-ml-[1px]" />}
          isRecentChatList
          menuItems={[]}
          isOpen={recentChatMenuIsOpen}
          toggleIsOpen={() => {
            toggleRecentChatIsOpen();
            handleMenuClick('Recentes', 'menu_toggle');
          }}
          onItemClick={(item) => handleMenuClick('Recentes', item.name)}
          resetChat={resetChat}
        />
        <SidebarButtons
          resetChat={resetChat}
          newChat={newChat}
          sidebarIsOpen={sidebarIsOpen}
        />
      </ul>

      {isMobile() && (
        <button
          onClick={() => setSidebarIsOpen(false)}
          className="glass-pane"></button>
      )}
    </aside>
  )
}
